/**
 * Project items API envelope (see `store/test.js`).
 * GET body: `{ success?: boolean, data: NestedNode[] }`
 */

/**
 * @param {unknown} node
 * @returns {object}
 */
function normalizeNode(node) {
  if (!node || typeof node !== "object") return node;
  const rawChildren = node.children;
  const children = Array.isArray(rawChildren)
    ? rawChildren.map((child) => normalizeNode(child))
    : [];

  if (
    node.item_type === "product" &&
    node.product &&
    typeof node.product === "object"
  ) {
    const p = node.product;
    const { product: _embed, children: _c, ...rest } = node;
    return {
      ...rest,
      children,
      product_id: rest.product_id != null ? rest.product_id : p.id,
      sku: p.sku,
      name:
        rest.name != null && String(rest.name).trim() !== ""
          ? rest.name
          : p.name,
      images: p.images,
      size: p.size,
      range: p.range,
      against_order: p.against_order,
      archived: p.archived === true,
      price: rest.price ?? null,
      discount_price: rest.discount_price ?? null,
      price_stale: rest.price_stale === true,
      current_discount_price:
        p.current_discount_price != null &&
        !Number.isNaN(Number(p.current_discount_price))
          ? Number(p.current_discount_price)
          : null,
      variants: p.variants,
    };
  }

  return { ...node, children };
}

/**
 * @param {object[]} flat — from flattenProjectItemsTree
 * @returns {boolean}
 */
export function quoteHasArchivedProducts(flat) {
  if (!Array.isArray(flat)) return false;
  return flat.some(
    (item) => item?.item_type === "product" && item?.archived === true
  );
}

/**
 * @param {unknown} body — axios `response.data`
 * @returns {object[]}
 */
export function parseProjectItemsResponseBody(body) {
  const roots = body?.data.items;
  return Array.isArray(roots) ? roots.map((node) => normalizeNode(node)) : [];
}

/**
 * @param {unknown} body — axios `response.data`
 * @returns {string|null}
 */
export function getProjectItemsProjectStatus(body) {
  const raw = body?.data?.project?.status;
  if (raw == null) return null;
  const status = String(raw).trim().toLowerCase();
  return status || null;
}

/**
 * Whether the store project is linked to a dealer (buyer must have dealer at project snapshot).
 *
 * @param {unknown} body — axios `response.data`
 * @returns {boolean|undefined}
 */
export function getProjectHasDealer(body) {
  const v = body?.data?.project?.has_dealer;
  if (v === true) return true;
  if (v === false) return false;
  return undefined;
}

/**
 * @param {unknown} body — axios `response.data` from project items index
 * @returns {string|null} `"dealer"` | `"direct"` | null
 */
export function getProjectMode(body) {
  const mode = String(body?.data?.project?.mode ?? "").trim().toLowerCase();
  if (mode === "dealer" || mode === "direct") return mode;
  return null;
}

/**
 * @param {unknown} body — axios `response.data` from project items index
 * @returns {object|null}
 */
export function getProjectDealerOrder(body) {
  const row = body?.data?.project?.dealer_order;
  return row && typeof row === "object" ? row : null;
}

/**
 * @param {unknown} body — axios `response.data` from project items index
 * @returns {number}
 */
export function getProjectCreditAdjustmentsTotal(body) {
  const total = Number(body?.data?.project?.credit_adjustments_total);
  return Number.isFinite(total) && total > 0 ? total : 0;
}

/**
 * @param {unknown} body — axios `response.data` from project items index
 * @returns {boolean}
 */
export function getProjectItemsCanManageProjectItems(body) {
  return body?.data?.project?.can_manage_project_items === true;
}

export function getProjectItemsSample(body) {
  return body?.data?.project?.sample === true;
}

export function getProjectItemsActions(body) {
  const rawNext = body?.data?.actions?.next_status;
  const nextStatus =
    rawNext == null ? null : String(rawNext).trim().toLowerCase() || null;
  const access = body?.data?.actions?.access === true;
  return { nextStatus, access };
}

/**
 * @param {object[]} nodes
 * @param {number|null} [parentId]
 * @param {object[]} [out]
 * @returns {object[]}
 */
export function flattenProjectItemsTree(nodes, parentId = null, out = []) {
  if (!Array.isArray(nodes)) return out;
  for (const node of nodes) {
    const children = Array.isArray(node.children) ? node.children : [];
    out.push({
      ...node,
      parent_id:
        node.parent_id === undefined || node.parent_id === null
          ? parentId
          : node.parent_id,
      children,
    });
    flattenProjectItemsTree(children, node.id, out);
  }
  return out;
}

/** Immutable copy of nested tree (for React state / useMemo). */
export function cloneProjectItemsTree(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node) => ({
    ...node,
    children: cloneProjectItemsTree(node.children),
  }));
}

/** Parse API body → display tree + flat list for moves/updates. */
export function projectItemsBodyToTreeAndFlat(body) {
  const roots = parseProjectItemsResponseBody(body);
  const tree = cloneProjectItemsTree(roots);
  const flat = flattenProjectItemsTree(tree);
  return { tree, flat };
}

/**
 * @param {object[]} nodes
 * @param {number|string} id
 * @param {object|null} parentNode
 * @returns {{ list: object[], index: number, parent: object|null }|null}
 */
function findNodeListIndexAndParent(nodes, id, parentNode = null) {
  if (!Array.isArray(nodes)) return null;
  for (let i = 0; i < nodes.length; i++) {
    if (Number(nodes[i].id) === Number(id)) {
      return { list: nodes, index: i, parent: parentNode };
    }
    const ch = nodes[i].children;
    if (Array.isArray(ch) && ch.length > 0) {
      const hit = findNodeListIndexAndParent(ch, id, nodes[i]);
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * @param {object[]} nodes
 * @param {number|string} id
 * @returns {object|null}
 */
function findDeepNode(nodes, id) {
  if (!Array.isArray(nodes)) return null;
  for (const n of nodes) {
    if (Number(n.id) === Number(id)) return n;
    const hit = findDeepNode(n.children, id);
    if (hit) return hit;
  }
  return null;
}

function renumberSiblingList(list, parentId) {
  if (!Array.isArray(list)) return;
  const pid = parentId === undefined || parentId === null ? null : parentId;
  for (let i = 0; i < list.length; i++) {
    list[i].position = i;
    list[i].parent_id = pid;
  }
}

/**
 * Optimistic cart reorder: move `dragId` to the slot of `targetItemId` (same semantics as
 * PATCH with target's parent_id + position). Mutates a fresh clone; returns it or null.
 *
 * @param {object[]} tree — root array (cloned)
 * @param {number|string} dragId
 * @param {number|string} targetItemId
 * @returns {object[]|null}
 */
export function applyOptimisticCartReorder(tree, dragId, targetItemId) {
  const next = cloneProjectItemsTree(tree);
  const dragLoc = findNodeListIndexAndParent(next, dragId);
  const targetLoc = findNodeListIndexAndParent(next, targetItemId);
  if (!dragLoc || !targetLoc) return null;
  if (Number(dragId) === Number(targetItemId)) return null;

  const [moved] = dragLoc.list.splice(dragLoc.index, 1);
  const tLoc = findNodeListIndexAndParent(next, targetItemId);
  if (!tLoc) return null;

  const parentId = tLoc.parent?.id ?? null;
  moved.parent_id = parentId;
  tLoc.list.splice(tLoc.index, 0, moved);

  renumberSiblingList(tLoc.list, parentId);
  if (dragLoc.list !== tLoc.list) {
    const oldPid = dragLoc.parent?.id ?? null;
    renumberSiblingList(dragLoc.list, oldPid);
  }
  return next;
}

/**
 * Optimistic move under a folder or to cart root.
 * Root (`newParentId == null`): appends to root list (matches API when no position is sent).
 * Under a folder: inserts at `insertAtChildIndex` (0 = first child), then renumbers positions.
 *
 * @param {object[]} tree — root array (cloned)
 * @param {number|string} dragId
 * @param {number|string|null} newParentId — `null` for root
 * @param {number} [insertAtChildIndex] — index among new parent's children before insert; default 0
 * @returns {object[]|null}
 */
export function applyOptimisticCartMoveToParent(
  tree,
  dragId,
  newParentId,
  insertAtChildIndex = 0
) {
  const next = cloneProjectItemsTree(tree);
  const dragLoc = findNodeListIndexAndParent(next, dragId);
  if (!dragLoc) return null;

  const [moved] = dragLoc.list.splice(dragLoc.index, 1);
  const oldPid = dragLoc.parent?.id ?? null;
  renumberSiblingList(dragLoc.list, oldPid);

  if (newParentId == null) {
    moved.parent_id = null;
    next.push(moved);
    renumberSiblingList(next, null);
    return next;
  }

  const parent = findDeepNode(next, newParentId);
  if (!parent || parent.item_type !== "folder") return null;

  if (!Array.isArray(parent.children)) parent.children = [];
  moved.parent_id = Number(newParentId);
  const n = parent.children.length;
  const rawIdx = Number(insertAtChildIndex);
  const idx = Number.isFinite(rawIdx)
    ? Math.max(0, Math.min(Math.trunc(rawIdx), n))
    : 0;
  parent.children.splice(idx, 0, moved);
  renumberSiblingList(parent.children, Number(newParentId));
  return next;
}
